import React, { useCallback, useMemo, useState } from 'react';
import { RefreshCw, Download, Filter, ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import { useBoqMeasurements, sumMeasureForLine } from '../hooks/useBoqMeasurements';
import { useBoqProjectItems } from '../hooks/useBoqProjectItems';
import { useBoqLibrary } from '../hooks/useBoqLibrary';
import { useBoqTaxonomies } from '../hooks/useBoqTaxonomies';
import {
  BrioCard,
  BrioButton,
  BrioEmptyState,
  BrioSelect,
} from '../../components/BrioComponents';
import { canonicalTradesFor } from '../utils/canonicalTrades';
import { groupMeasurementsBySubtrade } from '../utils/measurementSections';
import MeasurementTable from './MeasurementTable';
import { ToolbarIcon } from './ToolbarIcon';

function flattenZones(tree, depth = 0, acc = []) {
  tree.forEach((z) => {
    acc.push({ id: z.id, code: z.code, label: `${'— '.repeat(depth)}${z.code} · ${z.label}` });
    if (z.children?.length) flattenZones(z.children, depth + 1, acc);
  });
  return acc;
}

function csvEscape(v) {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function MeasurementsTab({ boqProjectId, zoneTree, schemeStage }) {
  const {
    measurements, loading, error, refresh,
    updateMeasurement, deleteMeasurement, deleteMeasurements, createMeasurementsWithLinks,
  } = useBoqMeasurements(boqProjectId);
  const { items, updateItem } = useBoqProjectItems(boqProjectId);
  // Library + taxonomy give each line its canonical chapter / sub-trade so the
  // take-off can be organised exactly like the priced Bill and the Summary.
  const { chapterByItem, subtradeByItem, tradeGroupByItem, loading: libLoading } = useBoqLibrary();
  const { taxonomies } = useBoqTaxonomies();
  const canonicalTrades = useMemo(
    () => canonicalTradesFor(taxonomies, schemeStage),
    [taxonomies, schemeStage]
  );

  const [itemFilter, setItemFilter] = useState('');
  const [zoneFilter, setZoneFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [quick, setQuick] = useState('');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  // Group the sheet into trade → sub-trade sections (on by default). Off gives
  // the flat, fully-sortable list. Collapsed section keys live here so the
  // table stays a dumb renderer of the flattened, collapse-aware list.
  const [groupBySubtrade, setGroupBySubtrade] = useState(true);
  const [collapsedKeys, setCollapsedKeys] = useState(() => new Set());
  const toggleCollapse = useCallback((key) => {
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const activeFilterCount = (itemFilter ? 1 : 0) + (zoneFilter ? 1 : 0) + (levelFilter ? 1 : 0);
  const clearFilters = () => { setItemFilter(''); setZoneFilter(''); setLevelFilter(''); };

  const zoneOptions = useMemo(() => flattenZones(zoneTree || []), [zoneTree]);
  const zoneLabelById = useMemo(() => {
    const m = new Map();
    zoneOptions.forEach((z) => m.set(z.id, z.label));
    return m;
  }, [zoneOptions]);

  const itemById = useMemo(() => {
    const m = new Map();
    items.forEach((it) => m.set(it.id, it));
    return m;
  }, [items]);

  const codeForItem = useCallback((id) => itemById.get(id)?.version?.fullCode || '?', [itemById]);

  const lineBasisById = useMemo(() => {
    const m = new Map();
    items.forEach((it) => m.set(it.id, it.measureBasis || null));
    return m;
  }, [items]);

  const levels = useMemo(() => {
    const s = new Set();
    measurements.forEach((m) => { if (m.level) s.add(m.level); });
    return [...s].sort();
  }, [measurements]);

  const recomputeMany = useCallback(async (itemIds, source) => {
    const list = source || measurements;
    for (const itemId of new Set(itemIds)) {
      const basis = lineBasisById.get(itemId);
      if (!basis) continue;
      const q = sumMeasureForLine(list, basis, itemId) ?? 0;
      // eslint-disable-next-line no-await-in-loop
      await updateItem(itemId, { quantity: q }, { recordUndo: false });
    }
  }, [measurements, lineBasisById, updateItem]);

  const filtered = useMemo(() => {
    const q = quick.trim().toLowerCase();
    return measurements.filter((m) => {
      if (itemFilter && !m.itemIds.includes(itemFilter)) return false;
      if (zoneFilter && m.projectZoneId !== zoneFilter) return false;
      if (levelFilter && m.level !== levelFilter) return false;
      if (!q) return true;
      return [m.description, m.level, m.gridRef, m.locationNote]
        .some((x) => (x || '').toLowerCase().includes(q));
    });
  }, [measurements, itemFilter, zoneFilter, levelFilter, quick]);

  // Grouping needs the library's canonical chapter / sub-trade labels; until it
  // loads, fall back to the flat list rather than mis-bucketing everything.
  const grouping = groupBySubtrade && !libLoading;

  // Trade → sub-trade sections over the filtered rows (mirrors the Bill grid).
  const sections = useMemo(() => {
    if (!grouping) return null;
    return groupMeasurementsBySubtrade(filtered, items, {
      canonicalTrades, chapterByItem, subtradeByItem, tradeGroupByItem,
    });
  }, [grouping, filtered, items, canonicalTrades, chapterByItem, subtradeByItem, tradeGroupByItem]);

  // Flatten the sections into the render list the table consumes, dropping the
  // rows (and nested band headers) of any collapsed section.
  const renderList = useMemo(() => {
    if (!sections) return null;
    const out = [];
    sections.forEach((sec) => {
      const collapsed = collapsedKeys.has(sec.key);
      out.push({ kind: 'chapter', key: sec.key, label: sec.label, count: sec.count, collapsed, isUnlinked: sec.isUnlinked });
      if (collapsed) return;
      sec.directRows.forEach((m) => out.push({ kind: 'row', m }));
      sec.bands.forEach((band) => {
        const bandCollapsed = collapsedKeys.has(band.key);
        out.push({ kind: 'subtrade', key: band.key, label: band.label, count: band.rows.length, collapsed: bandCollapsed });
        if (!bandCollapsed) band.rows.forEach((m) => out.push({ kind: 'row', m }));
      });
    });
    return out;
  }, [sections, collapsedKeys]);

  // Every collapsible section / band key — drives the expand / collapse-all button.
  const collapsibleKeys = useMemo(() => {
    if (!sections) return [];
    const keys = [];
    sections.forEach((sec) => { keys.push(sec.key); sec.bands.forEach((b) => keys.push(b.key)); });
    return keys;
  }, [sections]);
  const allCollapsed = collapsibleKeys.length > 0 && collapsibleKeys.every((k) => collapsedKeys.has(k));
  const toggleCollapseAll = useCallback(() => {
    setCollapsedKeys((prev) => {
      const everyCollapsed = collapsibleKeys.length > 0 && collapsibleKeys.every((k) => prev.has(k));
      return everyCollapsed ? new Set() : new Set(collapsibleKeys);
    });
  }, [collapsibleKeys]);

  const handleCommit = useCallback(async (id, draft) => {
    const m = measurements.find((x) => x.id === id);
    const result = await updateMeasurement(id, draft);
    if (result.success) {
      const next = measurements.map((x) => (x.id === id ? result.measurement : x));
      await recomputeMany(m?.itemIds || [], next);
    }
    return result;
  }, [measurements, updateMeasurement, recomputeMany]);

  const handleDelete = useCallback(async (id) => {
    const m = measurements.find((x) => x.id === id);
    const result = await deleteMeasurement(id);
    if (result.success) {
      const next = measurements.filter((x) => x.id !== id);
      await recomputeMany(m?.itemIds || [], next);
    }
    return result;
  }, [measurements, deleteMeasurement, recomputeMany]);

  const handleDeleteMany = useCallback(async (ids) => {
    const idSet = new Set(ids);
    const affected = new Set();
    measurements.forEach((m) => { if (idSet.has(m.id)) m.itemIds.forEach((x) => affected.add(x)); });
    const result = await deleteMeasurements(ids);
    if (result.success) {
      const next = measurements.filter((m) => !idSet.has(m.id));
      await recomputeMany([...affected], next);
    }
    return result;
  }, [measurements, deleteMeasurements, recomputeMany]);

  const handleCreateRows = useCallback(async (inputs) => {
    const res = await createMeasurementsWithLinks(inputs);
    if (res.success) {
      const next = [...measurements, ...(res.measurements || [])];
      const affected = new Set();
      (res.measurements || []).forEach((m) => m.itemIds.forEach((x) => affected.add(x)));
      await recomputeMany([...affected], next);
    }
    return res;
  }, [createMeasurementsWithLinks, measurements, recomputeMany]);

  // Cell-range paste. New overflow rows have no line link here (the
  // Measurements tab isn't scoped to a line), so they feed nothing until
  // linked from a line's take-off.
  const handleApplyPaste = useCallback(async ({ updates, newRows }) => {
    let working = measurements;
    for (const u of updates) {
      const cur = working.find((m) => m.id === u.id);
      if (!cur) continue;
      const merged = {
        description: cur.description, nr: cur.nr, length: cur.length, breadth: cur.breadth, height: cur.height,
        projectZoneId: cur.projectZoneId, level: cur.level, gridRef: cur.gridRef, locationNote: cur.locationNote,
        ...u.fields,
      };
      // eslint-disable-next-line no-await-in-loop
      const res = await updateMeasurement(u.id, merged);
      if (res.success) working = working.map((m) => (m.id === u.id ? res.measurement : m));
    }
    if (newRows.length) {
      const res = await createMeasurementsWithLinks(newRows.map((o) => ({ ...o, itemIds: [] })));
      if (res.success) working = [...working, ...res.measurements];
    }
    const affected = new Set();
    updates.forEach((u) => { const m = working.find((x) => x.id === u.id); if (m) m.itemIds.forEach((id) => affected.add(id)); });
    await recomputeMany([...affected], working);
    return { success: true };
  }, [measurements, updateMeasurement, createMeasurementsWithLinks, recomputeMany]);

  const handleExportCsv = useCallback(() => {
    const header = ['Feeds', 'Description', 'Nr', 'L', 'B', 'H', 'Run m', 'Plan m²', 'Elev m²', 'Vol m³', 'Zone', 'Level', 'Grid', 'Note'];
    const lines = [header.join(',')];
    filtered.forEach((m) => {
      lines.push([
        m.itemIds.map(codeForItem).join(' '),
        m.description, m.nr, m.length, m.breadth, m.height,
        m.runningM, m.planArea, m.elevationArea, m.volume,
        zoneLabelById.get(m.projectZoneId) || '', m.level, m.gridRef, m.locationNote,
      ].map(csvEscape).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `boq-measurements-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }, [filtered, codeForItem, zoneLabelById]);

  if (!boqProjectId) {
    return <BrioCard><BrioEmptyState>No take-off yet — add measurements from a line's Take-off button in the Bill tab.</BrioEmptyState></BrioCard>;
  }
  if (loading) return <BrioCard><div style={{ color: 'var(--brio-text-muted)', padding: '0.5rem' }}>Loading measurements…</div></BrioCard>;
  if (error) {
    return <BrioCard><div style={{ color: '#a94442' }}>{error}</div><BrioButton onClick={refresh}>Try again</BrioButton></BrioCard>;
  }

  return (
    <BrioCard>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        <input type="search" placeholder="Filter…" value={quick} onChange={(e) => setQuick(e.target.value)}
          style={{ flex: '0 0 180px', padding: '0.3rem 0.55rem', border: '1px solid var(--brio-border, #ccc)', borderRadius: 4, fontSize: '0.85rem' }} />
        <div style={{ position: 'relative' }}>
          <ToolbarIcon onClick={() => setShowFilterMenu((v) => !v)} active={activeFilterCount > 0 || showFilterMenu} tooltip="Filter by line / zone / level">
            <Filter size={15} />
            {activeFilterCount > 0 && (
              <span style={{ position: 'absolute', top: -2, right: -2, minWidth: 14, height: 14, padding: '0 3px', borderRadius: 7, background: 'var(--brio-gold, #9F902A)', color: '#fff', fontSize: 9, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>{activeFilterCount}</span>
            )}
          </ToolbarIcon>
          {showFilterMenu && (
            <>
              <div onClick={() => setShowFilterMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 900 }} />
              <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 1000, background: '#fff', border: '1px solid var(--brio-border, #ccc)', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', borderRadius: 4, padding: '0.6rem 0.7rem', width: 230, fontFamily: 'Raleway, sans-serif' }}>
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  <label style={{ fontSize: '0.75rem', color: 'var(--brio-text-muted)' }}>
                    Line
                    <BrioSelect value={itemFilter} onChange={(e) => setItemFilter(e.target.value)} style={{ fontSize: '0.82rem', width: '100%', marginTop: '0.2rem' }}>
                      <option value="">All</option>
                      {items.map((it) => <option key={it.id} value={it.id}>{it.version?.fullCode}</option>)}
                    </BrioSelect>
                  </label>
                  <label style={{ fontSize: '0.75rem', color: 'var(--brio-text-muted)' }}>
                    Zone
                    <BrioSelect value={zoneFilter} onChange={(e) => setZoneFilter(e.target.value)} style={{ fontSize: '0.82rem', width: '100%', marginTop: '0.2rem' }}>
                      <option value="">All</option>
                      {zoneOptions.map((z) => <option key={z.id} value={z.id}>{z.label}</option>)}
                    </BrioSelect>
                  </label>
                  <label style={{ fontSize: '0.75rem', color: 'var(--brio-text-muted)' }}>
                    Level
                    <BrioSelect value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)} style={{ fontSize: '0.82rem', width: '100%', marginTop: '0.2rem' }}>
                      <option value="">All</option>
                      {levels.map((l) => <option key={l} value={l}>{l}</option>)}
                    </BrioSelect>
                  </label>
                  {activeFilterCount > 0 && (
                    <button type="button" onClick={clearFilters}
                      style={{ marginTop: '0.1rem', padding: '0.3rem', fontSize: '0.78rem', border: '1px solid var(--brio-border, #ccc)', borderRadius: 4, background: 'transparent', cursor: 'pointer', color: 'var(--brio-text-muted)', fontFamily: 'Raleway, sans-serif' }}>
                      Clear filters
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.15rem', alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => setGroupBySubtrade((v) => !v)}
            title="Organise the take-off into trade → sub-trade sections (like the Bill). Turn off for a flat, sortable list."
            style={{
              fontSize: '0.78rem', padding: '0.3rem 0.7rem', borderRadius: 4, cursor: 'pointer', marginRight: '0.2rem',
              border: '1px solid var(--brio-border, #ccc)', fontFamily: 'Raleway, sans-serif',
              background: groupBySubtrade ? 'var(--brio-gold, #9F902A)' : 'transparent',
              color: groupBySubtrade ? '#fff' : 'var(--brio-text, #333)',
            }}
          >
            Group by sub-trade
          </button>
          {grouping && collapsibleKeys.length > 0 && (
            <ToolbarIcon onClick={toggleCollapseAll} tooltip={allCollapsed ? 'Expand all sections' : 'Collapse all sections'}>
              {allCollapsed ? <ChevronsUpDown size={15} /> : <ChevronsDownUp size={15} />}
            </ToolbarIcon>
          )}
          <span style={{ fontSize: '0.82rem', color: 'var(--brio-text-muted)', alignSelf: 'center', marginRight: '0.35rem' }}>{filtered.length} of {measurements.length}</span>
          <ToolbarIcon onClick={refresh} tooltip="Refresh">
            <RefreshCw size={15} />
          </ToolbarIcon>
          <ToolbarIcon onClick={handleExportCsv} disabled={measurements.length === 0} tooltip="Export CSV">
            <Download size={15} />
          </ToolbarIcon>
        </div>
      </div>

      {measurements.length === 0 ? (
        <BrioEmptyState>
          No measurements in this project yet. Open a line's <strong>Take-off</strong> in the Bill tab to start.
        </BrioEmptyState>
      ) : (
        <MeasurementTable
          rows={filtered}
          zoneOptions={zoneOptions}
          basis=""
          showFeeds
          codeForItem={codeForItem}
          defaultItemIds={[]}
          onCommit={handleCommit}
          onDelete={handleDelete}
          onCreateRows={handleCreateRows}
          onDeleteMany={handleDeleteMany}
          onApplyPaste={handleApplyPaste}
          renderList={renderList}
          onToggleGroup={toggleCollapse}
          emptyHint="No measurements match the current filter."
        />
      )}
    </BrioCard>
  );
}

export default MeasurementsTab;
